import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    ViewChild,
    TemplateRef,
    SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from 'app/app.service';
import { IKuiSelectOption, IKuiTableCol, IKuiTableRow, IKuiDropdownMenuItem } from 'app/key-ui';
import { KuiOptionPanelComponent, KuiOptionListItem } from 'app/key-ui/option-panel/option-panel.component';
import { SortingEvent } from 'app/key-ui/table/table.component';
import { FilterChangeEvent } from 'app/pages/status/overview/overview.component';
import { AssetFilterService, AssetListItem, MatchMediaService, MeasurementUnitsService, LocalStore, AssetGroupingService } from 'app/services';

import { BaseComponent } from 'app/shared/components';
import { getIconSvgString, MAP_COLORS, MAP_COLORS_ENUM } from 'app/shared/components/map/map.markers';
import { findNested, NestableItem } from 'app/shared/utils/find-nested';

import * as moment from 'moment-timezone';
import { Observable, fromEvent, interval, BehaviorSubject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';
import { OverviewStateService } from '../../services/state.service';
import { AssetStateProfileListResponse, TelemetryIOValue } from '@key-telematics/fleet-api-client';
import { TopNavSettings } from 'app/key-ui/top-nav';
import { SpatialUtils } from 'app/shared/components/map/map.utils';
import { FilterPanelConfig } from 'app/shared/components/filter-panel/filter-panel.model';
import { FilterPanelService } from 'app/shared/components/filter-panel/filter-panel.service';

interface IKuiTableColValue extends IKuiTableCol {
    value?: string;
}

interface OptionListItemValued extends KuiOptionListItem {
    value?: any;
    id?: number | string;
    children?: OptionListItemValued[];
}

interface ColumnItem {
    key: string;
    name: string;
    value?: string;
    type?: string; // helps knowing where to look for the value i.e. io, profile etc.
    color?: string;
}

interface OverviewListState {
    tableColumns: { key: string, width?: string }[];
    showFilter: boolean;
}

/**
 * List item decorated with KuiTableRow
 */
export interface ListItem extends AssetListItem, IKuiTableRow {
    icon: string;
    name: string;
    last_update: IKuiTableColValue;
    listFilter?: any;
    hasGeoLock?: string;
    assetCostCentre?: string;
    assetGroups?: string;
    [key: string]: any;
    icons: {
        icon: string,
        name: string,
        active: boolean
    }[];
}

export const OverviewListLocalStore = new LocalStore('overview-list-component');

@Component({
    selector: 'key-overview-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    providers: [{ provide: LocalStore, useValue: OverviewListLocalStore }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewListComponent extends BaseComponent implements OnInit, OnChanges {
    isMobile: boolean;
    isFloating = false;
    isMobileOptionsOpen = false;
    isMobileFiltersOpen = false;
    searchIsOpen = false;
    searchValue: string;

    dateHighlightOptions: IKuiSelectOption[];
    dateFormattingOptions: IKuiSelectOption[];

    assetIndex: { [id: string]: ListItem } = {};
    assetListing: ListItem[];
    tableHeight: number;

    windowResize: Observable<Event> = fromEvent(window, 'resize').pipe(
        takeUntil(this.destroyed),
        startWith(new CustomEvent('resize')),
        debounceTime(100)
    );

    options: OptionListItemValued[];
    header: TopNavSettings;
    state: OverviewListState;

    columns: IKuiTableCol[] = [];
    columnSelectionDictionary: { [key: string]: ColumnItem } = {};
    columnSelectionMenu: IKuiDropdownMenuItem[];
    alwaysVisibleColumns: string[] = ['icon', 'name', 'last_update'];

    filterPanelConfig: FilterPanelConfig[];
    @Input() filterWidth = 250;

    // KeyAssetFilterComponent uses observable in order to register change through the dynamic component on desktop
    // so converting the filter input to an observable here instead of changing the whole filter type up the tree
    _filter: FilterChangeEvent[];
    filter$: BehaviorSubject<FilterChangeEvent[]> = new BehaviorSubject([]);
    @Input()
    get filter(): FilterChangeEvent[] {
        return this._filter || [];
    }
    set filter(filter: FilterChangeEvent[]) {
        this.filter$.next(filter);
        this._filter = filter;
    }

    @Input() layout: 'list' | 'table';
    @Input() title: string;
    @Input() show: boolean;
    @Input() count: number;
    @Input() listLoaded: boolean;
    @Input() sidebarIsOpen: boolean;
    @Input() activeAssetId: string;
    @Input() dateHighlightThreshold: number;
    @Input() dateFormatting: 'condensed' | 'full';
    @Input() tableWidth: string;
    @Input() sortField: string;
    @Input() sortOrder: 'asc' | 'desc';
    @Input() stateProfiles: { [clientId: string]: AssetStateProfileListResponse };
    @Input() errorMessage: string;
    @Input() page = 1;
    @Input() closable = false;

    get pageCount(): number {
        return (this.count / this.filters.limit);
    }

    // assets receive full list of assets at first and only smaller list of updates afterwards
    @Input() set assets(assets: AssetListItem[]) {
        this.updateColumnSelectionDictionary(assets);
        assets.forEach(item => {
            this.assetIndex[item.id] = this.createListItem(item);
        });
        this.assetListing = Object.keys(this.assetIndex).map(x => this.assetIndex[x]).sort((a, b) => {
            if (this.filters.point) {
                return (a.distance || 10000000000) - (b.distance || 10000000000);
            }
            return a.name.localeCompare(b.name);
        });

        this.updateColumns();
        this.updateColumnSelectionMenu();
    }

    @Output() mobileAction = new EventEmitter<any>();
    @Output() activeItemSelected = new EventEmitter<string>();
    @Output() filtersToggled = new EventEmitter<boolean>();
    @Output() searchTermChanged = new EventEmitter<string>();
    @Output() pageChanged = new EventEmitter<number>();
    @Output() layoutChanged = new EventEmitter<'list' | 'table'>();
    @Output() dateHighlightThresholdChanged = new EventEmitter<number>();
    @Output() dateFormattingChanged = new EventEmitter<'condensed' | 'full'>();
    @Output() filterChanged = new EventEmitter<FilterChangeEvent[]>();
    @Output() sortingChanged = new EventEmitter<SortingEvent>();

    @Output() onClose = new EventEmitter<void>();

    @ViewChild('desktopAssetFilterTemplate') assetFilterTemplate: TemplateRef<any>;
    @ViewChild('settingsMenu') settingsMenu: KuiOptionPanelComponent;
    @ViewChild('searchBar') searchBar: ElementRef;
    @ViewChild('contentContainer', { static: true }) container: ElementRef;

    // trigger set table height once the table actually exists inside the DOM
    @ViewChild('table') set table(_ref: ElementRef) {
        if (!_ref) {
            return;
        }

        setTimeout(() => {
            this.setTableHeight();
        });
    }

    handleInsideSidebarEvent(event: Event) {
        event.stopPropagation();
    }

    @HostListener('keyup', ['$event']) keyUp(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.hideSearchBar();
        }

        if (event.key === 'Enter') {
            this.submitSearch(this.searchValue);
        }
    }
    
    constructor(
        public app: AppService,
        private sanitizer: DomSanitizer,
        public filters: AssetFilterService,
        private filterPanelService: FilterPanelService,
        private measurement: MeasurementUnitsService,
        private i18n: TranslateService,
        private changeDetector: ChangeDetectorRef,
        private matchMedia: MatchMediaService,
        private overviewState: OverviewStateService,
        private assetGrouping: AssetGroupingService,
        private store: LocalStore
    ) {
        super();
        
        this.isFloating = this.app.features.page.overview.layout === 'basic';

        this.on(this.matchMedia.isDesktop, desktop => {
            this.isMobile = !desktop;
            this.updateHeader();
        });

        this.state = this.store.watchState<OverviewListState>('state', {
            tableColumns: [
                { key: 'icon', width: '30px' },
                { key: 'name', width: '100px' },
                { key: 'last_update', width: '100px' },
                { key: 'location', width: '400px' },
            ],
            showFilter: false,
        });

        this.on(this.filters.filters$, () => {
            this.searchValue = this.filters.getFilterValueByType('name');
        });

        this.on(this.filterPanelService.filters$, f => {

            const existing = this.filters.getFilters();
            const items = [];
            ['costcentre', 'assetgroup', 'assetcategory', 'assettype'].forEach(key => {
                if (Array.isArray(f.filters[key])) {
                    const value = f.filters[key][0].id;
                    const old = existing.filters.find(x => x.type === key);
                    if (!old || old.value !== value) {
                        this.filters.setFilter(key as any, value);
                    }
                    if (value) {
                        items.push({ type: key, id: value });
                    }
                }
            });
            this.updateColumns();
            this.filtersChanged(items);
        });

        this.on(this.app.client$, () => {
            this.updateFilterPanel(); // make sure we reload our filter panel if the client changes
        });

    }

    ngOnInit() {

        this.options = [];

        this.windowResize.subscribe(() => this.setTableHeight());

        setTimeout(() => {
            this.searchValue = this.filters.getFilterValueByType('name');

            if (this.filter.length) {
                this.filter.forEach(filterItem => {
                    this.filters.setFilter(filterItem.type, filterItem.id);
                });
            }
        });

        interval(30e3)
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
                this.refreshLastUpdateValues();
            });

        this.updateFilterPanel();

    }


    updateFilterPanel() {
        this.filterPanelConfig = [
            {
                id: 'assettype',
                title: this.i18n.instant('VIDEOS.FILTERS.ASSET_TYPE'),
                type: 'radio',
                collapsed: false,
                searchable: false,
                getData: async (_searchTerm: string) => {
                    const items = AssetGroupingService.treeToComboValues(await this.assetGrouping.getAssetTypesAsTree(), 1).map(item => ({
                        id: item.key,
                        name: item.value,
                        level: item.indent,
                    }));
                    return {
                        items: [
                            { id: undefined, name: this.i18n.instant('SHARED.ANY'), order: 0, level: 0 },
                            ...items.map((x, i) => ({ ...x, order: i })),
                        ],
                        limit: 100,
                        count: 0,
                        offset: 0,
                    };
                },
            },
            {
                id: 'costcentre',
                title: this.i18n.instant('VIDEOS.FILTERS.COST_CENTRE'),
                type: 'radio',
                collapsed: false,
                searchable: false,
                getData: async (_searchTerm: string) => {
                    const items = AssetGroupingService.treeToComboValues(await this.assetGrouping.getCostCentresAsTreeForUser(), 0).map(item => ({
                        id: item.key,
                        name: item.value,
                        level: item.indent,
                    }));
                    return {
                        items: items.map((x, i) => ({ ...x, order: i })),
                        limit: 100,
                        count: 0,
                        offset: 0,
                    };
                },
            },
            {
                id: 'assetgroup',
                title: this.i18n.instant('VIDEOS.FILTERS.ASSET_GROUP'),
                type: 'radio',
                collapsed: false,
                searchable: false,
                getData: async (_searchTerm: string) => {
                    const items = AssetGroupingService.treeToComboValues(await this.assetGrouping.getAssetGroupsAsTree(), 1).map(item => ({
                        id: item.key,
                        name: item.value,
                        level: item.indent,
                    }));
                    return {
                        items: [
                            { id: undefined, name: this.i18n.instant('SHARED.ANY'), order: 0, level: 0 },
                            ...items.map((x, i) => ({ ...x, order: i })),
                        ],
                        limit: 100,
                        count: 0,
                        offset: 0,
                    };
                },
            },
            {
                id: 'assetcategory',
                title: this.i18n.instant('VIDEOS.FILTERS.ASSET_CATEGORY'),
                type: 'radio',
                collapsed: false,
                searchable: false,
                getData: async (_searchTerm: string) => {
                    const items = AssetGroupingService.treeToComboValues(await this.assetGrouping.getAssetCategoriesAsTree(), 1).map(item => ({
                        id: item.key,
                        name: item.value,
                        level: item.indent,
                    }));
                    return {
                        items: [
                            { id: undefined, name: this.i18n.instant('SHARED.ANY'), order: 0, level: 0 },
                            ...items.map((x, i) => ({ ...x, order: i })),
                        ],
                        limit: 100,
                        count: 0,
                        offset: 0,
                    };
                },
            },
        ];
        this.changeDetector.markForCheck();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.dateHighlightThreshold || changes.dateFormatting || changes.layout || changes.title) {
            this.updateCurrentAssetListing();
            this.updateHeader();
        }
    }

    updateHeader() {
        this.header = {
            title: this.title,
            navigationAction: null,
            actions: [
                { id: 'search', icon: 'search', name: this.i18n.instant('SHARED.SEARCH'), click: () => this.toggleSearchbar() },
                // {
                //     id: 'filter', icon: 'filter', name: this.i18n.instant('SHARED.FILTERS'),
                //     ...(!this.isMobile ? { template: this.assetFilterTemplate } : { click: () => this.resetFiltersView() }),
                // },
                this.layout === 'table' && { id: 'columns', name: this.i18n.instant('STATUS.LIST.ACTION.COLUMNS'), icon: 'columns', menu: this.columnSelectionMenu },
                !this.isFloating && {
                    id: 'settings', icon: 'ellipsis-v', name: this.i18n.instant('SHARED.SETTINGS'),
                    ...({ click: () => this.resetOptionsView() }),
                },
                {
                    id: 'filter', icon: 'filter', name: this.i18n.instant('SHARED.FILTERS'),
                    active: this.state?.showFilter,
                    click: () => this.toggleFilters(),
                },
                this.closable && {
                    id: 'close', icon: 'times', name: this.i18n.instant('DIALOG.CLOSE'),
                    click: () => { this.onClose.emit(); },
                },
            ].filter(x => x),
        };

        const gridOptions = [
            { name: this.i18n.instant('STATUS.LIST.GRID.COLUMN'), value: 'list', selected: this.layout === 'list' },
            { name: this.i18n.instant('STATUS.LIST.GRID.GRID'), value: 'table', selected: this.layout === 'table' },
        ];

        this.dateHighlightOptions = [
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.NONE'), value: '0' },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.2_MINUTES'), value: String(60 * 2) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.5_MINUTES'), value: String(60 * 5) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.10_MINUTES'), value: String(60 * 10) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.15_MINUTES'), value: String(60 * 15) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.30_MINUTES'), value: String(60 * 30) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.45_MINUTES'), value: String(60 * 45) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.1_HOUR'), value: String(60 * 60) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.3_HOURS'), value: String(60 * 60 * 3) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.6_HOURS'), value: String(60 * 60 * 6) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.9_HOURS'), value: String(60 * 60 * 9) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.12_HOURS'), value: String(60 * 60 * 12) },
            { name: this.i18n.instant('STATUS.LIST.HIGHLIGHT.24_HOURS'), value: String(60 * 60 * 24) },
        ].map(item => ({ ...item, selected: String(this.dateHighlightThreshold) === item.value }));

        this.dateFormattingOptions = [
            { name: this.i18n.instant('STATUS.LIST.FORMATTING.CONDENSED'), value: 'condensed' },
            { name: this.i18n.instant('STATUS.LIST.FORMATTING.FULL'), value: 'full' },
        ].map(item => ({ ...item, selected: this.dateFormatting === item.value })),

        this.options = [
            {
                name: this.i18n.instant('STATUS.LIST.ACTION.LAYOUT'),
                children: gridOptions,
                selection: 'single',
                id: 'grid',
            },
            {
                name: this.i18n.instant('STATUS.LIST.ACTION.HIGHLIGHT'),
                children: this.dateHighlightOptions,
                selection: 'single',
                id: 'highlight',
            },
            {
                name: this.i18n.instant('STATUS.LIST.ACTION.DATE_FORMATTING'),
                children: this.dateFormattingOptions,
                selection: 'single',
                id: 'format',
            },
        ];
    }

    updateCurrentAssetListing() {
        this.assetListing = this.assetListing.map(item => {
            const asset = this.createListItem(item);
            this.assetIndex[item.id] = asset;
            return asset;
        });
    }

    onColumnsReorder(cols: IKuiTableCol[]) {
        // update state with correct order
        this.state.tableColumns = cols.map(x => ({ key: x.field, width: x.width as string }));
    }

    onColumnsResize(column: IKuiTableCol) {
        const index = this.state.tableColumns.findIndex(({ key }) => key === column.field);
        // would've been nice to just change this.state.tableColumns[index], but state obj needs to be replaced to register the update
        this.state.tableColumns = this.state.tableColumns.map((state, idx) => {
            return idx === index ? { ...state, width: column.width as string } : state;
        });
    }

    updateColumns() {

        // fix for clients that already have the tablecolumns saved and don't include the 'pin' value
        if (!this.state.tableColumns.find(x => x.key === 'pin')) {
            this.state.tableColumns.push({ key: 'pin', width: '100px' });
        }

        this.columns = this.state.tableColumns.filter(({ key }) => {
            if (key === 'pin') {
                return !!this.filters.point;
            }
            return this.columnSelectionDictionary[key] || this.alwaysVisibleColumns.find(x => x === key);
        }).map(({ key, width }) => {
            const item = this.columnSelectionDictionary[key];
            switch (key) {
                case 'icon':
                    return { display: '', field: 'icon', sortable: false, width };
                case 'name':
                    return { display: this.i18n.instant(`STATUS.LIST.COLUMN.NAME`), field: 'name', width };
                case 'last_update':
                    return {
                        display: this.i18n.instant(`STATUS.LIST.COLUMN.LAST_UPDATE`), field: 'last_update', width, compare: (_col: IKuiTableCol, row1: any, row2: any) => {
                            const placeholder = this.sortOrder === 'asc' ? '0' : 'Z';
                            const val1: string = row1.last_update.value || placeholder;
                            const val2: string = row2.last_update.value || placeholder;
                            return val2.localeCompare(val1);
                        },
                    };
                case 'pin':
                    return {
                        display: this.i18n.instant(`STATUS.LIST.COLUMN.PIN`), field: 'distance', width, align: 'right',
                        format: (_col: IKuiTableCol, row: any, _asExport?: boolean) => {
                            return row.distance !== undefined ? this.measurement.format(row.distance, 'distance').format : '';
                        },
                        compare: (_col: IKuiTableCol, row1: any, row2: any) => {
                            return (row1.distance || 10000000000) - (row2.distance || 10000000000);
                        },
                    };
                case 'speed':
                case 'speedLimit':
                    return {
                        display: item.name, field: item.key, width, compare: (_col: IKuiTableCol, row1: any, row2: any) => {
                            // convert values to number and compare those
                            const value1 = parseInt((row1[key] || '0').replace(/\D+/g, ''), 10);
                            const value2 = parseInt((row2[key] || '0').replace(/\D+/g, ''), 10);
                            return value1 - value2;
                        },
                    };
                default:
                    return { display: item.name, field: item.key, width };
            }
        });
    }

    getIOColumnKey(io: TelemetryIOValue): string {
        return 'io_' + io.name.toLowerCase().replace(/ /g, '_');
    }

    updateColumnSelectionDictionary(assets: AssetListItem[]) {
        assets.forEach(asset => {
            if (!this.columnSelectionDictionary['location']) {
                this.columnSelectionDictionary['location'] = { key: 'location', name: this.i18n.instant('STATUS.LIST.COLUMN.LOCATION') };
            }

            if (!this.columnSelectionDictionary['assetCostCentre']) {
                this.columnSelectionDictionary['assetCostCentre'] = { key: 'assetCostCentre', name: this.i18n.instant('STATUS.LIST.COLUMN.COST_CENTRE') };
            }

            if (!this.columnSelectionDictionary['assetGroups']) {
                this.columnSelectionDictionary['assetGroups'] = { key: 'assetGroups', name: this.i18n.instant('STATUS.LIST.COLUMN.ASSET_GROUPS') };
            }

            if (!this.columnSelectionDictionary['speedLimit']) {
                this.columnSelectionDictionary['speedLimit'] = { key: 'speedLimit', name: this.i18n.instant('STATUS.LIST.COLUMN.SPEED_LIMIT') };
            }

            if (asset.telemetry) {

                if (asset.telemetry.io) {
                    Object.keys(asset.telemetry.io).forEach(key => {
                        const io = asset.telemetry.io[key];
                        const columnKey = this.getIOColumnKey(io);
                        if (!this.columnSelectionDictionary[columnKey]) {
                            this.columnSelectionDictionary[columnKey] = {
                                key: columnKey,
                                name: io.name,
                                type: 'io',
                            };
                        }
                    });
                }

                if (asset.telemetry.state && this.stateProfiles) {
                    const profiles = this.stateProfiles[asset.owner.id];
                    if (profiles) {
                        Object.keys(asset.telemetry.state).filter(key => !this.columnSelectionDictionary[key]).forEach(key => {
                            const profile = profiles.items.find(x => x.id === key);
                            if (profile) {
                                const state = asset.telemetry.state[key];
                                const item = profile.items.find(x => x.state === state.state);
                                if (item) {
                                    this.columnSelectionDictionary[key] = {
                                        key,
                                        name: profile.name,
                                        type: 'state',
                                    };
                                }
                            }
                        });
                    }
                }
            }

            if (asset.fields && this.app.client.customFields) {
                const customFields = [
                    ...(this.app.client.customFields.asset || []),
                    ...(this.app.client.customFields[asset.assetType.id] || []),
                ];

                Object.keys(asset.fields).filter(key => !this.columnSelectionDictionary[key]).forEach(key => {
                    const cf = customFields.find(x => x.id === key);
                    if (cf) {
                        this.columnSelectionDictionary[key] = {
                            key,
                            name: cf.title,
                            type: 'field',
                        };
                    }
                });
            }

            this.columnSelectionDictionary = {
                ...this.columnSelectionDictionary,
                ...this.overviewState.getSanitizedTelemetry(asset.telemetry, asset.id),
            };

            // FUTURE DUDE: leaving this code here in case support ever requests for the telemetry bucket items to be part of the column selection list

            // let telemetry = {};

            // if (this.columnSelectionMap['date']) { // if one sanitizedTelemetry exists, all of them exists... no need to add them again
            //     telemetry = this.overviewState.getSanitizedTelemetry(asset.telemetry);
            // }

            // if (asset.telemetry.telemetry) {
            //     Object.keys(asset.telemetry.telemetry).filter(key => !this.columnSelectionMap[key]).forEach(key => {
            //         telemetry[key] = { key, name: key };
            //     });
            // }

            // this.columnSelectionMap = {
            //     ...this.columnSelectionMap,
            //     ...telemetry,
            // };

        });

    }

    updateColumnSelectionMenu() {
        const prev = (this.columnSelectionMenu || []).length;
        this.columnSelectionMenu = [
            { type: 'action', icon: 'undo', text: this.i18n.instant('STATUS.LIST.COLUMN.RESET_COLUMNS'), action: this.resetColumns.bind(this) },
            { type: 'divider' },
            ...Object.values(this.columnSelectionDictionary).map(({ key, name }) => ({
                type: 'toggle',
                text: name,
                checked: !!this.state.tableColumns.find(x => x.key === key),
                action: (e) => this.toggleColumnVisibility(key, e.target.checked),
            })).sort((a, b) => a?.text.localeCompare(b.text)),
        ];
        // shallow check if something actually changed and only then update the header
        if (prev !== this.columnSelectionMenu.length) {
            this.updateHeader();
        }
    }

    toggleColumnVisibility(key: string, checked: boolean) {
        if (checked) {
            this.state.tableColumns.push({ key, width: '100px' });
        } else {
            this.state.tableColumns.splice(this.state.tableColumns.findIndex(x => x.key === key), 1);
        }
        // mutating the array like above does not register the changes to state, so I am assigning a new array in order to update LocalStore
        this.state.tableColumns = [...this.state.tableColumns];

        this.updateCurrentAssetListing();
        this.updateColumns();
    }

    resetColumns() {
        this.state.tableColumns = [
            { key: 'icon', width: '30px' },
            { key: 'name', width: '100px' },
            { key: 'last_update', width: '100px' },
            { key: 'pin', width: '100px' },
            { key: 'location', width: '400px' },
        ];
        this.updateColumns();
        this.updateColumnSelectionMenu();
        this.updateHeader();
    }

    createListItem(asset: AssetListItem): ListItem {


        let geoLockStatus: string = null;
        if (asset.telemetry && asset.telemetry.location && asset.geoLock) {
            const distance = SpatialUtils.getDistanceFromLatLonInKm(asset.telemetry.location.lat, asset.telemetry.location.lon, asset.geoLock.lat, asset.geoLock.lon);
            geoLockStatus = distance > asset.geoLock.radiusKm ? 'warning' : 'active';
        } else {
            if (asset.telemetry?.telemetry?.geo_lock !== undefined) {
                geoLockStatus = asset.telemetry.telemetry.geo_lock === 1 ? 'warning' : 'active';
            }
        }

        // for now we only adding an icon for video, but maybe in the future we might want to add icons for other io types,
        // in that case we could remove this line and maybe add a function with a switch statement that would push an icon based on the io type to the list.
        let icons = [];
        if (asset && asset.telemetry && Object.keys(asset.telemetry.io).length > 0 && Object.keys(asset.telemetry.io).find(x => asset.telemetry.io[x].type === 'camera')) {
            icons = [{ icon: 'video', name: this.i18n.instant('STATUS.LIST.ICONS.VIDEO'), active: false }];
        }

        const result = {
            ...asset,
            icon: this.getMapIcon(asset, this.layout === 'list' ? 32 : 22, geoLockStatus),
            name: asset.name,
            last_update: this.createLastUpdate(asset),
            listFilter: asset.name,
            hasGeoLock: geoLockStatus,
            icons: icons,
        };

        const telemetry = this.overviewState.getSanitizedTelemetry(asset.telemetry, asset.id);

        // add all other selected columns and their values
        this.state.tableColumns.filter(({ key }) => !this.alwaysVisibleColumns.find(x => x === key)).forEach(({ key }) => {
            if (key === 'location') {
                result[key] = asset.telemetry && asset.telemetry.location ? asset.telemetry.location.address : '';
            } else if (key === 'assetCostCentre') {
                result[key] = asset.costCentre.name;
            } else if (key === 'assetGroups') {
                result[key] = asset.groups.map(x => x.name).join(', ');
            } else if (key === 'speedLimit') {
                const spd = asset.telemetry && asset.telemetry.spd;
                if (spd) {
                    result[key] = this.overviewState.getClientSpecificSpeedValue(spd.md || spd.rd, spd.un ? 'milesPerHour' : 'kilometresPerHour', 'speed');
                }
            } else {
                const item = this.columnSelectionDictionary[key];
                if (item) {
                    switch (item.type) {
                        case 'field':
                            result[key] = asset.fields[key];
                            break;
                        case 'io':
                            const ios = asset.telemetry?.io;
                            if (ios) {

                                const input = Object.keys(ios).find(x => {
                                    return (this.getIOColumnKey(ios[x]) === key);
                                });

                                const ioItem = asset.telemetry && asset.telemetry.io && asset.telemetry.io[input];

                                if (ioItem && ioItem.name === item.name) {
                                    if (ioItem.type === 'temperature') {
                                        const measurementUnit = this.measurement.format(ioItem.value, 'temperature');
                                        result[key] = (Math.round(measurementUnit.value as number * 10) / 10) + ' ' + (ioItem.unit || measurementUnit.unit || '');
                                    } else if (ioItem.type === 'output') {
                                        result[key] = ioItem.text || '';
                                    } else {
                                        result[key] = ioItem.value + ' ' + (ioItem.unit || '');
                                    }
                                }
                            }
                            break;
                        case 'state':
                            const profiles = this.stateProfiles[asset.owner.id];
                            const profile = profiles && profiles.items.find(x => x.id === key);

                            if (profile) {
                                const state = asset.telemetry && asset.telemetry.state && asset.telemetry.state[key];
                                if (state) {
                                    result[key] = {
                                        icon: { name: 'tag', color: state.color },
                                        display: state.state,
                                    };
                                }
                            }
                            break;
                        default:
                            // if we get here it is most probably a telemetry item, but we check the asset keys first just in case
                            result[key] = asset[key] || telemetry[key] && telemetry[key].value; /* FUTURE DUDE: uncomment this if support requests for the telemetry bucket items to be part of the column selection list:   || asset.telemetry && asset.telemetry.telemetry && asset.telemetry.telemetry[key] */
                            break;
                    }
                }
            }
        });

        return result;
    }

    createLastUpdate(asset: AssetListItem): IKuiTableColValue {
        if (asset.telemetry && asset.telemetry.date) {
            const dateEpoch = this.getDateEpoch(asset.telemetry.date);
            return { // Transform into a KuiRow-compatible format
                display: this.getDisplayDateString(asset),
                field: dateEpoch,
                value: dateEpoch,
            };
        }
        return { display: '', field: '', value: '' };
    }

    getDisplayDateString(asset: AssetListItem): string {
        return asset.telemetry && asset.telemetry.date ? `<span class="${this.shouldHighlightAssetDate(asset) ? 'text-danger' : ''}">${this.getDateFormat(asset.telemetry.date)}</span>` : '';
    }

    refreshLastUpdateValues() {
        let cnt = 0;
        // go through each asset, compare it's display string and if it's changed, update the last_update value
        this.assetListing.forEach(asset => {
            const displayString = this.getDisplayDateString(asset);
            if (asset.last_update.display !== displayString) {
                asset.last_update.display = displayString;
                cnt++;
            }
        });
        if (cnt > 0) {
            this.changeDetector.markForCheck();
        }
    }

    setTableHeight() {
        if (this.container) {
            this.tableHeight = this.container.nativeElement.clientHeight;
            this.changeDetector.markForCheck();
        }
    }

    submitSearch(value: string): void {
        if (value && value !== '') {
            this.assetIndex = {};
            this.hideSearchBar();
            this.searchTermChanged.emit(value);
        }
    }

    reset() {
        this.assetIndex = {};
        this.assetListing = [];
    }

    clearSearch(): void {
        this.searchValue = '';
        this.submitSearch('');
    }

    toggleSidebar(): void {
        this.sidebarIsOpen = !this.sidebarIsOpen;
    }

    toggleFilters(): void {
        this.state.showFilter = !this.state.showFilter;
        this.filtersToggled.emit(this.state.showFilter);
        this.updateHeader();
    }

    toggleSearchbar(visible?: boolean): void {
        this.searchIsOpen = visible !== undefined ? visible : !this.searchIsOpen;
        this.changeDetector.markForCheck();
        setTimeout(() => {
            if (this.searchIsOpen) {
                this.searchBar.nativeElement.focus();
            }
        });
    }

    hideSearchBar(): void {
        this.searchIsOpen = false;
    }

    selectAsset = (asset: ListItem): void => {
        if (asset.telemetry && asset.telemetry.location) {
            this.activeItemSelected.emit(asset.id);
        }
    }

    /**
     * Accepts the select change
     *
     * Note: the parameter is an array with one element since it's a single select.
     * There would have been multiple values were it a multiselect.
     */
    dateHighlightChanged([{ value }]: { value: string }[]) {
        // search through thresholds and mark the relevant one
        this.dateHighlightOptions = this.dateHighlightOptions.map(item => ({ ...item, selected: item.value === value }));

        this.dateHighlightThresholdChanged.emit(Number(value));

        // close the sidebar
        this.sidebarIsOpen = false;
    }

    updateDateFormatting([{ value }]: { value: 'condensed' | 'full' }[]) {
        // search through thresholds and mark the relevant one
        this.dateFormattingOptions = this.dateFormattingOptions.map(item => ({ ...item, selected: item.value === value }));

        this.dateFormattingChanged.emit(value);

        // close the sidebar
        this.sidebarIsOpen = false;

    }

    shouldHighlightAssetDate(asset: AssetListItem) {
        const date = asset.telemetry ? asset.telemetry.date : false;

        if (!date || this.dateHighlightThreshold === 0) {
            return false;
        }

        return moment(moment()).diff(moment(date)) / 1000 > this.dateHighlightThreshold;
    }

    /**
     * Retrieves the correct map asset icon based on color, speed and heading
     */
    getMapIcon(asset: AssetListItem, size: number, geoLockStatus?: string): string {

        if (this.layout === 'table' && geoLockStatus) {
            return `<i class="icon icon-lock text-${geoLockStatus === 'warning' ? 'danger' : 'default'} p-1" style="font-size: 1.25rem;"></i>`;
        }

        if (!asset.telemetry) {
            return '';
        }

        let iconType = 'idle';
        if (asset.telemetry.active) {
            if (!!asset.telemetry.location && asset.telemetry.location.speed === 0) {
                iconType = 'stop';
            } else {
                iconType = 'direction';
            }
        }

        return getIconSvgString(iconType, {
            fill: ((typeof asset.color === 'number')
                ? MAP_COLORS[asset.color || 0]
                : MAP_COLORS[MAP_COLORS_ENUM[asset.color] || 0]),
            rotation: asset.telemetry.location && asset.telemetry.location.heading || 0,
            size: size,
            dropShadowAlpha: 0,
        });
    }

    toggleLayout = (): void => {
        this.layout = this.layout === 'list' ? 'table' : 'list';
        this.layoutChanged.emit(this.layout);
        this.sidebarIsOpen = false;
    }

    /**
     * Bypasses security mechanism for sanitizing HTML in templates
     */
    sanitize(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    /**
     * Helper to format dates
     */
    getDateFormat(date: string): string {
        const adjustedTime = moment.utc(date, 'YYYYMMDDHHmm').tz(moment.defaultZone.name);
        return this.dateFormatting === 'condensed'
            ? adjustedTime.fromNow()
            : `${this.measurement.format(adjustedTime, 'date').format} ${this.measurement.format(adjustedTime, 'time').format}`;
    }

    getDateEpoch(date: string): string {
        return moment.utc(date, 'YYYYMMDDHHmm').tz(moment.defaultZone.name).format('x');
    }

    optionChanged(selected: OptionListItemValued) {
        switch (selected.value) {
            case 'condensed':
            case 'full':
                this.updateDateFormatting([{ value: selected.value }]);
                break;
            case 'list':
            case 'table':
                this.layoutChanged.emit(selected.value);
                break;
            default:
                this.dateHighlightChanged([{ value: selected.value }]);
            
        }
    }

    filtersChanged(filters: FilterChangeEvent[]) {
        this.reset();
        this.filterChanged.emit(filters);
        this.isMobileFiltersOpen = false;
        this.updateHeader();
    }

    clearFilters(): boolean {
        this.filtersChanged([]);
        this.searchValue = '';
        this.filters.resetFilterValues();
        this.filterPanelService.resetFilters();
        return true;
    }

    getFilters(filter: FilterChangeEvent[]): { name: string; type: string; }[] {
        return filter.map(filterItem => {
            let value: NestableItem;

            switch (filterItem.type) {
                case 'costcentre':
                    value = findNested(filterItem.id, this.filters.costCentres || []);
                    return {
                        type: this.i18n.instant('STATUS.LIST.ACTION.COST_CENTRE'),
                        name: value ? value.name : '',
                    };

                case 'assetcategory':
                    value = findNested(filterItem.id, this.filters.assetCategories || []);
                    return {
                        type: this.i18n.instant('STATUS.LIST.ACTION.ASSET_CATEGORY'),
                        name: value ? value.name : '',
                    };

                case 'assetgroup':
                    value = findNested(filterItem.id, this.filters.assetGroups || []);
                    return {
                        type: this.i18n.instant('STATUS.LIST.ACTION.ASSET_GROUP'),
                        name: value ? value.name : '',
                    };

                case 'assettype':
                    value = findNested(filterItem.id, this.filters.assetTypes || []);
                    return {
                        type: this.i18n.instant('STATUS.LIST.ACTION.ASSET_TYPE'),
                        name: value ? value.name : '',
                    };
            }
        });
    }

    resetFiltersView() {
        this.isMobileFiltersOpen = !this.isMobileFiltersOpen;
        this.changeDetector.markForCheck();
    }

    resetOptionsView() {
        this.settingsMenu.toggle(event);

        if (this.isMobile) {
            this.isMobileOptionsOpen = !this.isMobileOptionsOpen;
            this.changeDetector.markForCheck();
        }
    }

    onPageChanged(page: number) {
        this.pageChanged.emit(page);
    }
}
